import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { CheckboxCell, CheckboxHeader } from '@/components/tables/contentFields';
import TableLoadingView from '@/components/tables/TableLoadingView';
import EmptyTableView from '@/components/tables/EmptyTableView';
import Table, { TableHeader, TableBody } from '@/components/tables/TableBasics';
import type { TranslationEntry } from '../types';
import { TranslationEditor } from './TranslationCell';

interface TranslationsTableProps {
  translations: TranslationEntry[];
  isLoading: boolean;
  onSave: (translationId: string, value: string | null) => Promise<void>;
  selectedIds: Set<string>;
  onSelectionChange: (ids: Set<string>) => void;
}

export function TranslationsTable({
  translations,
  isLoading,
  onSave,
  selectedIds,
  onSelectionChange,
}: TranslationsTableProps) {
  const { t } = useTranslation();

  const allSelected = translations.length > 0 && selectedIds.size === translations.length;
  const someSelected = selectedIds.size > 0 && selectedIds.size < translations.length;

  const handleSelectAll = useCallback(() => {
    onSelectionChange(allSelected ? new Set() : new Set(translations.map((e) => e.id)));
  }, [allSelected, translations, onSelectionChange]);

  const handleSelectRow = useCallback((id: string) => {
    const next = new Set(selectedIds);
    next.has(id) ? next.delete(id) : next.add(id);
    onSelectionChange(next);
  }, [selectedIds, onSelectionChange]);

  if (isLoading) {
    return <TableLoadingView isLoading={true} columns={5} rows={10} />;
  }

  if (translations.length === 0) {
    return <EmptyTableView />;
  }

  return (
    <Table className="min-h-0 h-full">
      <TableHeader>
        <CheckboxHeader
          checked={allSelected}
          onChange={handleSelectAll}
          ariaLabel={t('common.selectAll')}
          indeterminate={someSelected}
        />
        <th className="px-4 py-3 text-start text-xs font-medium text-onsurface/60 uppercase tracking-wider">
          {t('translations.table.columns.name')}
        </th>
        <th className="px-4 py-3 text-start text-xs font-medium text-onsurface/60 uppercase tracking-wider">
          {t('translations.table.columns.defaultValue')}
        </th>
        <th className="w-24 px-4 py-3 text-start text-xs font-medium text-onsurface/60 uppercase tracking-wider">
          {t('translations.table.columns.reviewed')}
        </th>
        <th className="px-4 py-3 text-start text-xs font-medium text-onsurface/60 uppercase tracking-wider">
          {t('translations.table.columns.translation')}
        </th>
      </TableHeader>
      <TableBody>
        {translations.map((entry) => (
          <tr key={entry.id} className="hover:bg-surface-darker/50">
            <CheckboxCell
              checked={selectedIds.has(entry.id)}
              onChange={() => handleSelectRow(entry.id)}
              ariaLabel={t('common.selectRow')}
            />
            <td className="px-4 py-3 text-sm text-onsurface">
              <div className="font-mono text-xs">{entry.name}</div>
              {entry.description && (
                <div className="text-xs text-onsurface/50 mt-1">{entry.description}</div>
              )}
            </td>
            <td className="px-4 py-3 text-sm text-onsurface">
              {entry.defaultValue}
            </td>
            <td className="px-4 py-3">
              {entry.isReviewed ? (
                <span className="inline-flex items-center rounded-full bg-green-100 px-2 py-0.5 text-xs font-medium text-green-700 dark:bg-green-900/30 dark:text-green-400">
                  {t('translations.table.badges.reviewed')}
                </span>
              ) : (
                <span className="inline-flex items-center rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700 dark:bg-amber-900/30 dark:text-amber-400">
                  {t('translations.table.badges.new')}
                </span>
              )}
            </td>
            <td>
              <TranslationEditor
                initialValue={entry.translationValue}
                onSave={async (value) => {
                  await onSave(entry.id, value || null);
                }}
              />
            </td>
          </tr>
        ))}
      </TableBody>
    </Table>
  );
}
